/**
 * Synthetic UOM reference data for the standalone (MSW) build — the same wire
 * shapes the live Oxbo-M / Oxbo-P UOM endpoints emit, so flipping
 * VITE_USE_MOCKS=false is a true drop-in. Only the drilling subset the demo
 * screens touch is modelled (length / penetration_rate / currency).
 */
import type { FieldUnitsMap, ProfileSummary, ResolvedProfile, ResolvedUnit } from "@units/types";

const U = (
  dimension_key: string,
  unit_key: string,
  symbol: string,
  b: number,
  a = 0,
): ResolvedUnit => ({
  dimension_key,
  unit_key,
  symbol,
  conv_a: a,
  conv_b: b,
  conv_c: 1,
  conv_d: 0,
});

/** api profile — imperial (matches m.dev `/v1/uom/profiles/api/resolved`). */
const API_UNITS: Record<string, ResolvedUnit> = {
  length: U("length", "ft", "ft", 0.3048),
  penetration_rate: U("penetration_rate", "ft/h", "ft/hr", 0.0000846667),
  pressure: U("pressure", "psi", "psi", 6.894757),
  mud_weight: U("mud_weight", "lbm/galUS", "ppg", 119.8264),
  volume: U("volume", "bbl", "bbl", 0.1589873),
  force_weight: U("force_weight", "lbf", "lbf", 4.448222),
  torque: U("torque", "lbf.ft", "ft·lbf", 1.355818),
  flow_rate: U("flow_rate", "bbl/h", "bbl/hr", 0.002649788),
  angle: U("angle", "dega", "°", 1),
};

/** metric profile — SI (length/rop are identity to base). */
const METRIC_UNITS: Record<string, ResolvedUnit> = {
  length: U("length", "m", "m", 1),
  penetration_rate: U("penetration_rate", "m/h", "m/hr", 1),
  pressure: U("pressure", "kPa", "kPa", 1000),
  mud_weight: U("mud_weight", "kg/m3", "kg/m³", 1),
  volume: U("volume", "m3", "m³", 1),
  force_weight: U("force_weight", "N", "N", 1),
  torque: U("torque", "N.m", "N·m", 1),
  flow_rate: U("flow_rate", "m3/min", "m³/min", 0.0166667),
  angle: U("angle", "dega", "°", 1),
};

export const RESOLVED_PROFILES: Record<string, ResolvedProfile> = {
  api: {
    profile_key: "api",
    display_name: "API / Field (imperial)",
    tier: "stock",
    units: API_UNITS,
  },
  metric: {
    profile_key: "metric",
    display_name: "Metric (SI)",
    tier: "stock",
    units: METRIC_UNITS,
  },
};

export const PROFILE_LIST: ProfileSummary[] = [
  {
    id: "profile-api",
    organization_id: null,
    profile_key: "api",
    display_name: "API / Field (imperial)",
    unit_map: { length: "ft", penetration_rate: "ft/h", pressure: "psi" },
    is_active: true,
  },
  {
    id: "profile-metric",
    organization_id: null,
    profile_key: "metric",
    display_name: "Metric (SI)",
    unit_map: { length: "m", penetration_rate: "m/h", pressure: "kPa" },
    is_active: true,
  },
];

export const FIELD_UNITS: FieldUnitsMap = {
  profile_classes: [
    "angle",
    "flow_rate",
    "force_weight",
    "length",
    "mud_weight",
    "penetration_rate",
    "pressure",
    "temperature",
    "torque",
    "volume",
  ],
  special_classes: {
    currency: "money — unit is the row's currency column; FX-converted",
    duration: "elapsed time — stored uniformly; never converted",
    count: "dimensionless counter — never converted",
  },
  fields: {
    daily_report: {
      md_current: { quantity_class: "length" },
      tvd_current: { quantity_class: "length" },
      rop_avg_day: { quantity_class: "penetration_rate" },
      daily_cost: { quantity_class: "currency" },
      pct_complete: { quantity_class: "count" },
    },
    event: {
      tvd_current: { quantity_class: "length" },
      cost_authorized: { quantity_class: "currency" },
    },
    assembly: {
      hole_size: { quantity_class: "length", unit_override: "in" },
      md_top: { quantity_class: "length" },
    },
    cost_line: {
      total: { quantity_class: "currency" },
      unit_price: { quantity_class: "currency" },
      qty: { quantity_class: "count" },
    },
    afe_line: { afe_amount: { quantity_class: "currency" } },
  },
};
